import axios from 'axios';
import GlobalTable from '@/components/probaworkflows_base_components/GlobalTable/GlobalTable.vue';
import NavigationBackButton from "@/components/probaworkflows_base_components/NavigationBackButton/NavigationBackButton.vue";
import alert_mixin from '@/utils/mixins/alert_mixin';
import formatting_mixins from '@/utils/mixins/formatting_mixins';

import { 
  LIST_RISK_MATRIX_TYPES, 
  LIST_RISK_FRAMEWORKS, 
  CREATE_RISK_FRAMEWORK,
  CREATE_RISK_TYPE,
  DEACTIVATE_FRAMEWORK,
  ACTIVATE_FRAMEWORK,
  VIEW_ENTITY_TYPE_URL,
  ASSIGN_FRAMEWORK_TO_ENTITY,
  get_headers,
  get_token,
  get_project_id
} from '@/utils/constants/constants.js';

export default {
  name: 'ManageRiskFramework',
  mixins: [alert_mixin, formatting_mixins],
  components: {
    GlobalTable,
    NavigationBackButton
  },
  data: () => ({
    risk_types: [],
    risk_frameworks: [],
    entities: [],
    new_risk_framework: {
      risk_framework_type_id: '',
      new_type_name: '',
      risk_assessment: '',
      description: '',
      assessment_phase: 'pre'
    },
    selected_entity_ids: [],
    assignment_payload: {
      assessment_id: '',
      entity_type_id: []
    },
    headers: get_headers(),
    token: get_token(),
    project_id: get_project_id(),
  }),
  computed: {
    risk_framework_table_data() {
      return this.risk_frameworks.map(framework => ({
        id: framework.id,
        type: framework.risk_framework_type.risk_type,
        risk_framework: framework.risk_assessment,
        description: framework.description,
        is_active: framework.is_active,
      }));
    }
  },
  mounted() {
    this.fetch_risk_types();
    this.fetch_risk_frameworks();
    this.fetch_entities();
  },
  methods: {
    fetch_risk_types() {
      axios
        .get(LIST_RISK_MATRIX_TYPES, { headers: this.headers })
        .then(({ data }) => {
          if (data.status === 'success') {
            this.risk_types = data.data;
          } else {
            this.show_error('Failed to load risk types');
          }
        })
        .catch(err => {
          this.show_error(`Error fetching risk types: ${err.response?.data?.message}`);
        });
    },
    fetch_risk_frameworks() {
      axios
        .get(LIST_RISK_FRAMEWORKS, { headers: this.headers,
          params: { project_id: this.project_id }
         })
        .then(({ data }) => {
          if (data.status === 'success') {
            this.risk_frameworks = data.data;
          } else {
            this.show_error('Failed to load risk frameworks');
          }
        })
        .catch(err => {
          this.show_error(`Error fetching frameworks: ${err.response?.data?.message}`);
        });
    },
    fetch_entities() {
      axios
        .get(VIEW_ENTITY_TYPE_URL, {
          headers: this.headers,
          params: { project_id: this.project_id }
        })
        .then(({ data }) => {
          if (data.status === 'success') {
            this.entities = data.data;
          } else {
            this.show_error('Failed to load entities');
          }
        })
        .catch(err => {
          this.show_error(`Error fetching entities: ${err.response?.data?.message}`);
        });
    },
    assign_framework_to_entity() {
      if (!this.assignment_payload.assessment_id || this.selected_entity_ids.length === 0) {
        this.show_error('Select both a framework and an entity.');
        return;
      }
      const payload = {
          assessment_id: this.assignment_payload.assessment_id,
          entity_type_ids: this.selected_entity_ids
      };
      axios
        .post(ASSIGN_FRAMEWORK_TO_ENTITY, payload, { headers: this.headers })
        .then(({ data }) => {
          if (data.status === 'success') {
            this.show_success('Risk framework assigned successfully');
            this.close_modal('assign_risk_framework_modal');
            this.assignment_payload = {
              assessment_id: '',
              entity_type_id: ''
            };
          } else {
            this.show_error(data.message || 'Error assigning framework');
          }
        })
        .catch(err => {
          this.show_error(`Error assigning framework: ${err.response?.data?.message}`);
        });
    },
    create_risk_framework() {

      if (this.new_risk_framework.risk_framework_type_id === 'other') {
        const type_name = this.new_risk_framework.new_type_name.trim();
        if (!type_name) {
          this.show_error('Please enter a name for the new risk type.');
          return;
        }

        const type_payload = { risk_type: type_name };

        axios
          .post(CREATE_RISK_TYPE, type_payload, { headers: this.headers })
          .then(type_res => {
            if (type_res.data.status !== 'success') {
              this.show_error('Error creating new risk type');
              return null;
            }
            const created_type_id = type_res.data.data.id;
            const payload = {
              risk_framework_type_id: created_type_id,
              risk_assessment: this.new_risk_framework.risk_assessment,
              description: this.new_risk_framework.description,
              project_id: this.project_id
            };

            return axios.post(CREATE_RISK_FRAMEWORK, payload, { headers: this.headers });
          })
          .then(framework_res => {
            if (!framework_res) return null;

            if (framework_res.data.status === 'success') {
              this.show_success('Risk framework created successfully');
              this.close_modal('create_risk_framework_modal');
              this.new_risk_framework = {
                risk_framework_type_id: '',
                new_type_name: '',
                risk_assessment: '',
                description: '',
              };
              this.fetch_risk_types();
              this.fetch_risk_frameworks();
            } else {
              this.show_error('Error creating risk framework');
            }
          })
          .catch(err => {
            const err_msg =
              err.response?.data?.message ||
              err.response?.data?.detail ||
              err.message;
            this.show_error(`Error: ${err_msg}`);
          });
      }
      else
        axios
          .post(
            CREATE_RISK_FRAMEWORK,
            {
              risk_framework_type_id: this.new_risk_framework.risk_framework_type_id,
              risk_assessment: this.new_risk_framework.risk_assessment,
              description: this.new_risk_framework.description,
              project_id: this.project_id
            },
            { headers: this.headers }
          )
          .then(({ data }) => {
            if (data.status === 'success') {
              this.show_success('Risk framework created successfully');
              this.close_modal('create_risk_framework_modal');
              this.fetch_risk_frameworks();
            } else {
              this.show_error('Error creating risk framework');
            }
          })
          .catch(err => {
            this.show_error(`Error creating framework: ${err.response?.data?.message}`);
          });
    },
    toggle_risk_framework(row) {
      const url = row.is_active
        ? DEACTIVATE_FRAMEWORK
        : ACTIVATE_FRAMEWORK
      axios
        .post(
          url,
          {},
          {
            headers: this.headers,
            params: { assessment_id: row.id }
          }
        )
        .then(({ data }) => {
          if (data.status === 'success') {
            this.show_success(
              row.is_active
                ? 'Risk framework deactivated'
                : 'Risk framework activated'
            )
            this.fetch_risk_frameworks()
          } else {
            this.show_error(data.message || 'Unexpected error')
          }
        })
        .catch(err => {
          this.show_error(err.response?.data?.message || err.message)
        })
    },
    redirect_to_manage_risk_questions(assessment_id) {
      sessionStorage.setItem("assessment_id", assessment_id);
      this.$router.push('/risk_questions')
    }
  }
};
